import React, { useEffect, useMemo, useState } from "react";
import useServiceStore from "../../../store/builders/useServiceStore";
import { Loader2 } from "lucide-react";

/**
 * ServicesTile – Modern UI (keeps your original functionality)
 * New per your request:
 * 1) In the Add modal, a small **All** button selects all available services
 * 2) Below the selector, a neat radio-style checklist shows what is selected
 *    (implemented with checkboxes so you can select many; styled round like radio)
 */

const SERVICE_LIMIT = 10;

// Utility hook to lock scroll when any modal is open
function useLockBodyScroll(lock) {
  useEffect(() => {
    if (lock) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [lock]);
}

export default function ServicesTile() {
  const {
    fetchServices,
    fetchBuilderService,
    allServices,
    builderServices,
    fetchCreateService,
    deleteService,
    addServiceToBuilder,
    isLoading,
  } = useServiceStore();

  const addFromDropdown = async () => {
    if (!dropdownServiceId) return;
    const id = parseInt(dropdownServiceId, 10);
    const currentCount = builderServices?.length || 0;
    if (currentCount >= SERVICE_LIMIT) {
      setIsLimitOpen(true);
      return;
    }
    await addServiceToBuilder(id);
    await fetchBuilderService();
    setDropdownServiceId("");
    setIsAddOpen(false);
  };

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isLimitOpen, setIsLimitOpen] = useState(false);

  const [newServiceName, setNewServiceName] = useState("");
  const [query, setQuery] = useState("");

  // multi-select state for adding services
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dropdownServiceId, setDropdownServiceId] = useState("");

  // multi-select state for removing services (batch delete)
  const [selectedToRemove, setSelectedToRemove] = useState(new Set());

  // Lock scroll if any modal is open
  useLockBodyScroll(isAddOpen || isCreateOpen || isLimitOpen);

  useEffect(() => {
    fetchServices();
    fetchBuilderService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Services you can still add (exclude already added)
  const selectableServices = useMemo(() => {
    const chosenIds = new Set((builderServices || []).map((s) => s.id));
    return (allServices || []).filter((s) => !chosenIds.has(s.id));
  }, [allServices, builderServices]);

  // Filter for the main list view
  const visibleBuilderServices = useMemo(() => {
    if (!query.trim()) return builderServices || [];
    const q = query.toLowerCase();
    return (builderServices || []).filter((s) => s.name.toLowerCase().includes(q));
  }, [builderServices, query]);

  // ===== Actions =====
  const addSelectedServices = async () => {
    try {
      const currentCount = builderServices?.length || 0;
      const toAdd = Array.from(selectedIds);

      if (currentCount >= SERVICE_LIMIT) {
        setIsLimitOpen(true);
        return;
      }

      // enforce limit
      const room = Math.max(0, SERVICE_LIMIT - currentCount);
      const limited = toAdd.slice(0, room);

      if (limited.length === 0) {
        setIsLimitOpen(true);
        return;
      }

      // Add in parallel but wait for all
      await Promise.all(
        limited.map(async (id) => {
          await addServiceToBuilder(id);
        })
      );

      await fetchBuilderService();
      setSelectedIds(new Set());
      setIsAddOpen(false);
    } catch (e) {
      console.error("Failed to add service(s)", e);
    }
  };

  const createNewService = async () => {
    const name = newServiceName.trim();
    if (!name) return;
    try {
      await fetchCreateService({ name });
      await fetchServices();
      await fetchBuilderService();
      setNewServiceName("");
      setIsCreateOpen(false);
    } catch (e) {
      console.error("Failed to create service", e);
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await deleteService(serviceId);
      await fetchServices();
      await fetchBuilderService();
    } catch (e) {
      console.error("Failed to delete service", e);
    }
  };

  const toggleRemoveSelection = (id) => {
    setSelectedToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const batchRemoveServices = async () => {
    if (selectedToRemove.size === 0) return;
    try {
      await Promise.all(
        Array.from(selectedToRemove).map((id) => deleteService(id))
      );
      await fetchServices();
      await fetchBuilderService();
      setSelectedToRemove(new Set());
    } catch (e) {
      console.error("Failed to delete selected services", e);
    }
  };

  // ESC to close modals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsAddOpen(false);
        setIsCreateOpen(false);
        setIsLimitOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Helpers for selection UI
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(selectableServices.map((s) => s.id)));
  };

  const clearAll = () => setSelectedIds(new Set());

  return (
    <div className="py-2 text-xs sm:text-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-4 border-b border-gray-400 pb-3">
        <h2 className="text-sm font-semibold">Services</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsCreateOpen(true)}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-xs transition-colors flex items-center gap-1.5"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
            Create Service
          </button>
          <button
            onClick={() => setIsAddOpen(true)}
            className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs transition-colors flex items-center gap-1.5"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Choose
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search added services…"
            className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none"
          />
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="grid grid-cols-[1fr_auto] bg-gray-50 px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 border-b">
          <div>Service</div>
          <div className="text-right">Action</div>
        </div>

        {isLoading ? (
          <div className="p-6 text-sm text-gray-500 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-blue-500" />
            Loading services…
          </div>
        ) : visibleBuilderServices.length === 0 ? (
          <EmptyState onAdd={() => setIsCreateOpen(true)} />
        ) : (
          <>
            {selectedToRemove.size > 0 && (
              <div className="flex items-center justify-between bg-blue-50 px-4 py-2 border-b border-blue-100">
                <div className="text-xs font-medium text-blue-700">
                  {selectedToRemove.size} selected
                </div>
                <button
                  onClick={batchRemoveServices}
                  className="px-3 py-1 bg-red-500 text-white text-[11px] font-semibold rounded hover:bg-red-600 transition"
                >
                  Remove Selected
                </button>
              </div>
            )}
            <ul className="divide-y divide-gray-100">
              {visibleBuilderServices.map((srv) => {
                const isChecked = selectedToRemove.has(srv.id);
                return (
                  <li key={srv.id} className={`flex justify-between items-center px-4 py-3 transition ${isChecked ? 'bg-blue-50/30' : 'hover:bg-gray-50/50'}`}>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleRemoveSelection(srv.id)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div className="text-sm font-medium text-gray-700">{srv.name}</div>
                    </div>
                    <button
                      onClick={() => handleDeleteService(srv.id)}
                      className="p-1 px-2.5 text-red-500 hover:text-white hover:bg-red-500 border border-red-500/20 rounded text-[11px] font-medium transition-all"
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Add Modal — default catalogue dropdown only */}
      {isAddOpen && (
        <Modal title="Add Service" onClose={() => { setIsAddOpen(false); setDropdownServiceId(""); }}>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-medium text-gray-600 block mb-1">
                Choose from Ducktail default services
              </label>
              <select
                value={dropdownServiceId}
                onChange={(e) => setDropdownServiceId(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 focus:border-blue-500 outline-none"
                disabled={selectableServices.length === 0}
              >
                <option value="">
                  {selectableServices.length === 0
                    ? "All default services already added"
                    : "Select a service…"}
                </option>
                {selectableServices.map((srv) => (
                  <option key={srv.id} value={srv.id}>
                    {srv.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              disabled={!dropdownServiceId || isLoading}
              onClick={addFromDropdown}
              className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-50 active:scale-[.99] transition"
            >
              Add Service
            </button>
            <p className="text-[11px] text-gray-500">
              Only admin-managed default services appear here. Use <b>Create Service</b> for custom offerings.
            </p>
          </div>
        </Modal>
      )}

      {/* Create Modal */}
      {isCreateOpen && (
        <Modal title="Create Service" onClose={() => setIsCreateOpen(false)}>
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-600">Service name</label>
            <input
              type="text"
              value={newServiceName}
              onChange={(e) => setNewServiceName(e.target.value.toUpperCase())}
              placeholder="e.g., Interior Design"
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />
            <button
              onClick={createNewService}
              className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:opacity-95 active:scale-[.99] transition"
            >
              Create
            </button>
          </div>
        </Modal>
      )}

      {/* Limit Modal */}
      {isLimitOpen && (
        <Modal title="Limit Reached" onClose={() => setIsLimitOpen(false)}>
          <div className="space-y-3 text-sm text-gray-700">
            <p>You’ve reached the maximum of {SERVICE_LIMIT} services.</p>
            <p className="text-gray-500">Remove one of the existing services to add a new one.</p>
            <button
              onClick={() => setIsLimitOpen(false)}
              className="w-full rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-black active:scale-[.99] transition"
            >
              Got it
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-md origin-top scale-100 animate-[fadeIn_.15s_ease-out] rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center p-10 text-center">
      <div className="mb-3 rounded-full bg-gray-100 p-3">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><rect x="3" y="4" width="18" height="12" rx="2" /><line x1="2" y1="20" x2="22" y2="20" /><path d="M6 8h12" /></svg>
      </div>
      <h3 className="text-sm font-semibold text-gray-900">No services yet</h3>
      <p className="mt-1 max-w-sm text-xs text-gray-500">Create your first service to get started, or add one from the existing list.</p>
      <button
        onClick={onAdd}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
      >
        Create Service
      </button>
    </div>
  );
}

/* Tailwind keyframes suggestion (optional)
keyframes: { fadeIn: { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } } },
animation: { fadeIn: 'fadeIn .15s ease-out' }
*/